import React, { ReactNode } from 'react';

import { FormattedMessage } from 'react-intl';

import { CoreUtils, IdEnums, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import commonMessages from '~/core/commonMessages';
import { SpecialEvent } from '~/core/tableColumns/scheduleTableColumns/enums';
import { getEventType } from '~/core/tableColumns/scheduleTableColumns/utils';
import { PlayoffSeriesInfo } from '~/core/types';
import { FormatPlayoffSeriesInfo } from '~/core/utils';

import { ScheduleGame } from '../../../../types';

import { PlayoffSeriesContainer } from './styles';

// For the day we need to know the minimum game state. That way we can show the appropriate columns
export const getMinimumGameState = (games: ScheduleGame[]): ShortCodeEnums.GameState => {
    if (games.some(({ gameState }) => CoreUtils.isPregame(gameState))) {
        return ShortCodeEnums.GameState.pregame;
    }
    if (games.some(({ gameState }) => CoreUtils.isGameLive(gameState))) {
        return ShortCodeEnums.GameState.live;
    }
    return ShortCodeEnums.GameState.official;
};

export const multipleGameStatesCheck = (games: ScheduleGame[]): boolean => {
    //For a day, we need to know if there are multiple games at different states
    const hasSeenType: { [key: string]: boolean } = {
        pregame: false,
        live: false,
        postGame: false,
    };

    for (const game of games) {
        if (CoreUtils.isPregame(game.gameState)) {
            hasSeenType.pregame = true;
        } else if (CoreUtils.isGameLive(game.gameState)) {
            hasSeenType.live = true;
        } else if (CoreUtils.isPostGame(game.gameState)) {
            hasSeenType.postGame = true;
        }

        const seenStatesCount = Object.values(hasSeenType).reduce<number>(
            (trueCount, hasSeen) => (hasSeen ? trueCount + 1 : trueCount),
            0
        );

        if (seenStatesCount >= 2) {
            return true;
        }
    }

    return false;
};

export const createPlayoffSeriesInfo = (game: ScheduleGame): ReactNode => {
    const series = game?.seriesStatus;
    if (!series) {
        return null;
    }

    const gameInfo = (
        <FormattedMessage
            {...commonMessages.playoffGameDescriptions.abbreviations.game}
            values={{ gameNumber: series.gameNumberOfSeries }}
        />
    );

    const playoffSeriesInfo: PlayoffSeriesInfo = {
        topSeedAbbrev: series.topSeedTeamAbbrev,
        topSeedWins: series.topSeedWins,
        bottomSeedAbbrev: series.bottomSeedTeamAbbrev,
        bottomSeedWins: series.bottomSeedWins,
        neededToWin: series.neededToWin,
    };

    const round = commonMessages.playoffRoundDescriptions.long[series.seriesAbbrev];
    return (
        <PlayoffSeriesContainer>
            <FormattedMessage
                {...commonMessages.playoffGameDescriptions.descriptions.roundAndGame}
                values={{
                    game: gameInfo,
                    round: round ? <CoreUtils.SafeFormattedMessage message={round} /> : '',
                }}
            />
            &nbsp;|&nbsp;
            <FormatPlayoffSeriesInfo playoffSeriesInfo={playoffSeriesInfo} />
        </PlayoffSeriesContainer>
    );
};

export const getEventColor = (eventType: SpecialEvent, theme): string => {
    if (eventType === SpecialEvent.playoffs) {
        return theme.Utility.stanleyCupGray;
    } else if (eventType === SpecialEvent.event) {
        return theme.Utility.accent;
    } else if (eventType === SpecialEvent.preseason) {
        return theme.Surface.tertiary;
    }
    return 'initial';
};

type EventDetails = {
    content: ReactNode;
    eventType: SpecialEvent;
    neutralIndicator: ReactNode;
};

export const getEventDetails = (game: ScheduleGame): EventDetails => {
    let content: ReactNode = null;
    let neutralIndicator: ReactNode = null;

    if (game.gameType === IdEnums.GameType.playoffs && game.seriesStatus) {
        content = createPlayoffSeriesInfo(game);
    } else if (game.gameType === IdEnums.GameType.preseason) {
        content = <FormattedMessage {...commonMessages.gameTypeDescriptions.long[IdEnums.GameType.preseason]} />;
    }

    if (game.neutralSite) {
        neutralIndicator =
            content !== null ? (
                <FormattedMessage {...commonMessages.gameLocationDescriptions.long.neutralSite} />
            ) : (
                <FormattedMessage
                    description="Describes a game's venue which is a neutral site, ie neither the home nor the away team's home venue."
                    defaultMessage="Neutral Site"
                />
            );
    }

    return {
        content,
        eventType: getEventType(game),
        neutralIndicator,
    };
};
